/**
 * ALCEDO Platform Console — Sprint 44
 *
 * Accessible to ALCEDO Operator (alcedo_operator) users only.
 * Manages ALL tenants, platform revenue, system health.
 *
 * Color scheme: dark navy sidebar (#0f172a) with slate accents
 * to distinguish from the tenant console (green).
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../lib/apiClient';
import { isAlcedoConsoleDomain } from '../utils/domain';

const PLATFORM_ROLES = new Set(['alcedo_operator']);

export default function PlatformConsolePage({ onClose, token, user }) {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [selectedTenant, setSelectedTenant] = useState(null);

  // DEFENSE-IN-DEPTH: Layer 4 per DOMAIN_AWARE_AUTHORIZATION.md §4.
  // Platform Console must NEVER render on customer domain.
  if (!isAlcedoConsoleDomain()) {
    console.error(
      '[SECURITY] PlatformConsolePage attempted to render on customer domain. Redirecting.'
    );
    if (onClose) onClose();
    return null;
  }

  if (!user || !PLATFORM_ROLES.has(user.role)) {
    return (
      <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-slate-950 flex items-center justify-center">
        <div className="text-center space-y-3 px-6">
          <div className="text-3xl">🔒</div>
          <p className="text-sm text-slate-400">{t('platform.accessDenied')}</p>
          <button onClick={onClose} className="text-xs text-blue-400 underline">
            {t('platform.backToApp')}
          </button>
        </div>
      </div>
    );
  }

  const SECTIONS = [
    { key: 'dashboard', label: t('platform.dashboard'), icon: '📊' },
    { key: 'partners', label: t('platform.partners', { defaultValue: 'Partner' }), icon: '🤝' },
    { key: 'tenants', label: t('platform.tenants'), icon: '🏢' },
    { key: 'revenue', label: t('platform.revenue'), icon: '💶' },
    { key: 'data_health', label: t('platform.dataHealth'), icon: '🩺' },
    { key: 'system', label: t('platform.system'), icon: '⚙️' },
    { key: 'audit', label: t('platform.auditLog'), icon: '📋' },
    { key: 'settings', label: t('platform.settings'), icon: '🔧' },
  ];

  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-slate-950 flex flex-col">
      {/* Header — dark navy, ALCEDO branding */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 flex-shrink-0 bg-slate-900">
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-800 transition text-slate-400"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold">A</div>
          <div>
            <span className="text-sm font-semibold text-white">{t('platform.title')}</span>
            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-600/30 text-blue-400">ALCEDO</span>
          </div>
        </div>
        <div className="ml-auto text-[10px] text-slate-500">{user.email}</div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — dark navy */}
        <nav className="w-48 border-r border-slate-800 bg-slate-900 flex-shrink-0 pt-2 hidden sm:block">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              onClick={() => { setActiveSection(s.key); setSelectedTenant(null); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition ${
                activeSection === s.key
                  ? 'bg-blue-600/20 text-blue-400 font-medium'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
              }`}
            >
              <span>{s.icon}</span>
              {s.label}
            </button>
          ))}
        </nav>

        {/* Mobile tab bar */}
        <div className="flex sm:hidden border-b border-slate-800 bg-slate-900 overflow-x-auto absolute bottom-0 left-0 right-0 z-10">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              onClick={() => { setActiveSection(s.key); setSelectedTenant(null); }}
              className={`flex-shrink-0 flex flex-col items-center gap-0.5 px-3 py-2 text-[9px] transition ${
                activeSection === s.key
                  ? 'text-blue-400 border-t-2 border-blue-500'
                  : 'text-slate-500'
              }`}
            >
              <span className="text-base">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Main + optional TenantDetail */}
        <div className="flex flex-1 overflow-hidden">
          <main className={`overflow-y-auto p-4 sm:p-6 transition-all ${selectedTenant ? 'w-1/2 hidden sm:block' : 'flex-1'}`}>
            {activeSection === 'dashboard' && (
              <PlatformDashboard token={token} t={t} onSelectTenant={id => { setSelectedTenant(id); setActiveSection('tenants'); }} />
            )}
            {activeSection === 'partners' && (
              <PlatformDistributionPartners token={token} t={t} />
            )}
            {activeSection === 'tenants' && (
              <PlatformTenants token={token} t={t} selectedId={selectedTenant} onSelect={setSelectedTenant} />
            )}
            {activeSection === 'revenue' && <PlatformRevenue token={token} t={t} />}
            {activeSection === 'data_health' && <PlatformDataHealth token={token} t={t} />}
            {activeSection === 'system' && <PlatformSystem token={token} t={t} />}
            {activeSection === 'audit' && <PlatformAuditLog token={token} t={t} />}
            {activeSection === 'settings' && <PlatformSettings token={token} t={t} />}
          </main>

          {/* Tenant detail panel */}
          {selectedTenant && (
            <div className="w-full sm:w-1/2 border-l border-slate-800 bg-slate-900 overflow-y-auto flex-shrink-0">
              <TenantDetailPanel
                token={token}
                t={t}
                tenantId={selectedTenant}
                onClose={() => setSelectedTenant(null)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Platform Dashboard ────────────────────────────────────────

function PlatformDashboard({ token, t, onSelectTenant }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/aif/platform/dashboard', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <PlatformSpinner />;
  if (!data) return <PlatformError message={t('platform.loadError')} />;

  const health = data.system_health || {};
  const tenantCounts = data.tenants || {};

  return (
    <div className="space-y-5 max-w-5xl">
      <div>
        <h2 className="text-lg font-semibold text-white">{t('platform.dashboard')}</h2>
        <p className="text-xs text-slate-500 mt-0.5">ALCEDO Management GmbH — Platform Overview</p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <PlatformKpi value={tenantCounts.total} label={t('platform.kpiTotalTenants')} icon="🏢" />
        <PlatformKpi value={tenantCounts.active} label={t('platform.kpiActiveTenants')} icon="✅" />
        <PlatformKpi value={data.customers_total} label={t('platform.kpiTotalCustomers')} icon="👥" />
        <PlatformKpi value={data.customers_active_30d} label={t('platform.kpiActiveCustomers30d')} icon="🟢" />
        <PlatformKpi
          value={`${(data.platform_mrr || 0).toLocaleString('de-DE')} €`}
          label={t('platform.kpiPlatformMrr')} icon="💶"
        />
        <PlatformKpi
          value={`${(data.platform_arr || 0).toLocaleString('de-DE')} €`}
          label={t('platform.kpiPlatformArr')} icon="📈"
        />
        <PlatformKpi
          value={`${(data.alcedo_revenue_share || 0).toFixed(2)} €`}
          label={t('platform.kpiAlcedoShare')} icon="🏦"
        />
        <PlatformKpi
          value={`${(data.platform_margin || 0).toFixed(2)} €`}
          label={t('platform.kpiMargin')}
          icon={data.platform_margin >= 0 ? '📊' : '⚠️'}
          accent={data.platform_margin >= 0 ? 'green' : 'red'}
        />
      </div>

      {/* System health */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 p-4">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
          {t('platform.systemHealth')}
        </h3>
        <div className="flex gap-6 flex-wrap">
          <HealthDot label={t('platform.healthDb')} status={health.db} />
          <HealthDot label={t('platform.healthRedis')} status={health.redis} />
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">{t('platform.healthContainers')}:</span>
            <span className="text-xs font-semibold text-white">{health.containers}</span>
          </div>
        </div>
      </div>

      {/* Per-tenant summary */}
      {data.per_tenant_summary && data.per_tenant_summary.length > 0 && (
        <div className="rounded-xl border border-slate-700 overflow-hidden">
          <div className="bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
            {t('platform.perTenantSummary')}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left px-3 py-2 text-slate-400">{t('platform.colTenantName')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colCustomers')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colMrr')}</th>
                <th className="text-right px-3 py-2 text-slate-400 hidden sm:table-cell">{t('platform.colAlcedoShare')}</th>
                <th className="text-right px-3 py-2 text-slate-400 hidden sm:table-cell">{t('platform.colApiCost')}</th>
                <th className="text-left px-3 py-2 text-slate-400">{t('platform.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {data.per_tenant_summary.map((t2, i) => (
                <tr
                  key={t2.id}
                  onClick={() => onSelectTenant(t2.id)}
                  className={`border-t border-slate-700 cursor-pointer hover:bg-slate-700/50 transition ${i % 2 === 0 ? 'bg-slate-900' : 'bg-slate-800/50'}`}
                >
                  <td className="px-3 py-2">
                    <span className="font-medium text-white">{t2.name}</span>
                    <span className="text-slate-500 ml-2 font-mono">{t2.slug}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-300">{t2.customers}</td>
                  <td className="px-3 py-2 text-right text-slate-300">{t2.mrr > 0 ? `${t2.mrr} €` : '—'}</td>
                  <td className="px-3 py-2 text-right text-blue-400 hidden sm:table-cell">
                    {t2.alcedo_share_eur > 0 ? `${t2.alcedo_share_eur.toFixed(2)} €` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-400 hidden sm:table-cell">
                    {t2.api_cost_30d > 0 ? `${t2.api_cost_30d.toFixed(4)} €` : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <TenantStatusBadge status={t2.status} t={t} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ── Platform Tenants ──────────────────────────────────────────

function PlatformTenants({ token, t, selectedId, onSelect }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    fetch('/api/v1/aif/platform/tenants', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const tenants = (data?.tenants || []).filter(t2 =>
    !search || t2.name.toLowerCase().includes(search.toLowerCase()) || (t2.slug || '').includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">{t('platform.tenants')}</h2>
        <button
          onClick={() => setShowCreate(s => !s)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-500 transition"
        >
          {showCreate ? '✕' : `+ ${t('platform.tenantCreate')}`}
        </button>
      </div>

      {showCreate && (
        <CreateTenantForm token={token} t={t} onCreated={() => { setShowCreate(false); load(); }} />
      )}

      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Suchen…"
        className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-500"
      />

      {loading ? <PlatformSpinner /> : (
        <div className="rounded-xl border border-slate-700 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left px-3 py-2 text-slate-400">{t('platform.colTenantName')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colCustomers')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colMrr')}</th>
                <th className="text-left px-3 py-2 text-slate-400 hidden sm:table-cell">{t('platform.colSplit')}</th>
                <th className="text-left px-3 py-2 text-slate-400">{t('platform.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {tenants.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">{t('platform.noTenants')}</td></tr>
              ) : tenants.map((tenant, i) => (
                <tr
                  key={tenant.id}
                  onClick={() => onSelect(tenant.id === selectedId ? null : tenant.id)}
                  className={`border-t border-slate-700 cursor-pointer transition ${
                    tenant.id === selectedId
                      ? 'bg-blue-600/10'
                      : i % 2 === 0 ? 'bg-slate-900 hover:bg-slate-800' : 'bg-slate-800/50 hover:bg-slate-700/50'
                  }`}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-white">{tenant.name}</div>
                    <div className="text-slate-500 font-mono">{tenant.slug}</div>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-300">{tenant.customers}</td>
                  <td className="px-3 py-2 text-right text-slate-300">
                    {tenant.mrr > 0 ? `${tenant.mrr} €` : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-400 hidden sm:table-cell">
                    {tenant.revenue_split_pct}% / {100 - tenant.revenue_split_pct}%
                  </td>
                  <td className="px-3 py-2">
                    <TenantStatusBadge status={tenant.status} t={t} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ── Create Tenant Form ────────────────────────────────────────

const FEATURE_OPTIONS = [
  { key: 'crop', labelKey: 'featureCrop' },
  { key: 'livestock', labelKey: 'featureLivestock' },
  { key: 'energy', labelKey: 'featureEnergy' },
  { key: 'direct_sales', labelKey: 'featureDirectSales' },
  { key: 'compliance', labelKey: 'featureCompliance' },
  { key: 'market', labelKey: 'featureMarket' },
];

const COUNTRY_OPTIONS = ['DE', 'AT', 'CH', 'PL', 'FR', 'GB', 'NL', 'IT', 'ES', 'IE', 'US', 'AU'];

function CreateTenantForm({ token, t, onCreated }) {
  const [form, setForm] = useState({
    name: '', slug: '', domain: '', revenue_split_pct: 50,
    app_name: 'AI Farm', primary_color: '#2D5F2B',
    max_customers: '', license_start: '', license_end: '',
    country_codes: ['DE'], features_enabled: {},
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const toggleCountry = (cc) => setForm(f => ({
    ...f,
    country_codes: f.country_codes.includes(cc)
      ? f.country_codes.filter(c => c !== cc)
      : [...f.country_codes, cc],
  }));

  const toggleFeature = (key) => setForm(f => ({
    ...f,
    features_enabled: { ...f.features_enabled, [key]: !f.features_enabled[key] },
  }));

  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(null);
    const payload = {
      name: form.name, slug: form.slug, domain: form.domain || undefined,
      revenue_split_pct: parseFloat(form.revenue_split_pct) || 50,
      app_name: form.app_name, primary_color: form.primary_color,
      country_codes: form.country_codes,
      features_enabled: form.features_enabled,
    };
    if (form.max_customers) payload.max_customers = parseInt(form.max_customers);
    if (form.license_start) payload.license_start = form.license_start;
    if (form.license_end) payload.license_end = form.license_end;

    try {
      await apiClient.post('/api/v1/aif/platform/tenants', payload, {
        entityType: 'tenant', queueOnOffline: true,
      });
      setSuccess(t('platform.tenantCreated'));
      setTimeout(() => onCreated(), 2000);
    } catch (err) {
      if (err instanceof ApiError) setError(err.body?.detail || 'Fehler');
      else setError('Verbindungsfehler');
    }
    setSaving(false);
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800 p-4 space-y-4">
      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('platform.tenantCreate')}</h3>

      <div className="grid grid-cols-2 gap-3">
        <PlFormRow label={t('platform.tenantName')}>
          <input
            type="text" value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: autoSlug(e.target.value) }))}
            className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white outline-none focus:border-blue-500"
          />
        </PlFormRow>
        <PlFormRow label={t('platform.tenantSlug')}>
          <input
            type="text" value={form.slug}
            onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white font-mono outline-none focus:border-blue-500"
          />
        </PlFormRow>
        <PlFormRow label={t('platform.tenantDomain')}>
          <input
            type="text" value={form.domain}
            onChange={e => setForm(f => ({ ...f, domain: e.target.value }))}
            placeholder="example.com"
            className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white outline-none focus:border-blue-500"
          />
        </PlFormRow>
        <PlFormRow label={`${t('platform.tenantRevenueSplit')} (%)`}>
          <input
            type="number" min="0" max="100" value={form.revenue_split_pct}
            onChange={e => setForm(f => ({ ...f, revenue_split_pct: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white outline-none focus:border-blue-500"
          />
        </PlFormRow>
        <PlFormRow label={t('platform.tenantAppName')}>
          <input
            type="text" value={form.app_name}
            onChange={e => setForm(f => ({ ...f, app_name: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white outline-none focus:border-blue-500"
          />
        </PlFormRow>
        <PlFormRow label={t('platform.tenantMaxCustomers')}>
          <input
            type="number" value={form.max_customers}
            onChange={e => setForm(f => ({ ...f, max_customers: e.target.value }))}
            placeholder="∞"
            className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white outline-none focus:border-blue-500"
          />
        </PlFormRow>
      </div>

      {/* Countries */}
      <PlFormRow label={t('platform.tenantCountries')}>
        <div className="flex gap-1.5 flex-wrap mt-1">
          {COUNTRY_OPTIONS.map(cc => (
            <button
              key={cc} onClick={() => toggleCountry(cc)}
              className={`px-2 py-0.5 rounded text-xs font-medium transition border ${
                form.country_codes.includes(cc)
                  ? 'bg-blue-600/30 text-blue-400 border-blue-500/30'
                  : 'bg-slate-900 text-slate-500 border-slate-700'
              }`}
            >
              {cc}
            </button>
          ))}
        </div>
      </PlFormRow>

      {/* Features */}
      <PlFormRow label={t('platform.tenantFeatures')}>
        <div className="grid grid-cols-3 gap-1.5 mt-1">
          {FEATURE_OPTIONS.map(f => (
            <label key={f.key} className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.features_enabled[f.key]}
                onChange={() => toggleFeature(f.key)}
                className="accent-blue-500"
              />
              {t(`platform.${f.labelKey}`)}
            </label>
          ))}
        </div>
      </PlFormRow>

      {error && <p className="text-xs text-red-400">{error}</p>}
      {success && <p className="text-xs text-green-400">✓ {success}</p>}

      <button
        onClick={handleSave}
        disabled={saving || !form.name || !form.slug}
        className="w-full py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 transition"
      >
        {saving ? '…' : t('platform.tenantSave')}
      </button>
    </div>
  );
}


// ── Tenant Detail Panel ───────────────────────────────────────

function TenantDetailPanel({ token, t, tenantId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editSplit, setEditSplit] = useState('');
  const [editFeatures, setEditFeatures] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);

  useEffect(() => {
    if (!tenantId || !token) return;
    setLoading(true);
    apiClient.get(`/api/v1/aif/platform/tenants/${tenantId}`)
      .then(r => {
        const d = r.data;
        setData(d);
        if (d) { setEditSplit(d.revenue_split_pct); setEditFeatures(d.features_enabled || {}); }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [tenantId, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await apiClient.patch(`/api/v1/aif/platform/tenants/${tenantId}`, { revenue_split_pct: parseFloat(editSplit), features_enabled: editFeatures }, {
        entityType: 'tenant', queueOnOffline: true,
      });
      if (r?.data) setData(r.data);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
  };

  const handleSuspend = async () => {
    try {
      await apiClient.delete(`/api/v1/aif/platform/tenants/${tenantId}`, {
        entityType: 'tenant', queueOnOffline: true,
      });
      setData(d => ({ ...d, status: 'suspended' }));
      setConfirmSuspend(false);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  if (loading) return <div className="p-4"><PlatformSpinner /></div>;
  if (!data) return <div className="p-4"><PlatformError message={t('platform.loadError')} /></div>;

  const TABS = [
    { key: 'overview', label: t('platform.tabOverview') },
    { key: 'revenue', label: t('platform.tabRevenue') },
    { key: 'features', label: t('platform.tabFeatures') },
    { key: 'customers', label: t('platform.tabCustomers') },
    { key: 'apikey', label: t('platform.apiKeySection') },
    { key: 'branding', label: t('platform.brandingTab') },
  ];

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-white">{data.name}</h3>
          <p className="text-[10px] text-slate-500 font-mono">{data.slug}</p>
          <TenantStatusBadge status={data.status} t={t} />
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300 p-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-700 gap-3">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`pb-2 text-xs transition ${
              activeTab === tab.key
                ? 'text-blue-400 border-b-2 border-blue-500 font-medium'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <PlMiniKpi label="MRR" value={data.mrr > 0 ? `${data.mrr} €` : '—'} />
            <PlMiniKpi label={t('platform.kpiTotalCustomers')} value={data.customers} />
            <PlMiniKpi label="API Kosten (30d)" value={data.api_cost_30d > 0 ? `${data.api_cost_30d.toFixed(4)} €` : '—'} />
            <PlMiniKpi label={t('platform.tenantApiKeyStatus')} value={data.has_api_key ? t('platform.tenantApiKeyConfigured') : t('platform.tenantApiKeyMissing')} />
          </div>

          {data.domain && (
            <PlDetailRow label={t('platform.tenantDomain')} value={data.domain} />
          )}
          {data.country_codes?.length > 0 && (
            <PlDetailRow label={t('platform.tenantCountries')} value={data.country_codes.join(', ')} />
          )}
          {data.license_start && (
            <PlDetailRow label={t('platform.tenantLicenseStart')} value={new Date(data.license_start).toLocaleDateString('de-DE')} />
          )}
          {data.license_end && (
            <PlDetailRow label={t('platform.tenantLicenseEnd')} value={new Date(data.license_end).toLocaleDateString('de-DE')} />
          )}

          {/* Suspend */}
          {data.status === 'active' && (
            <div className="pt-2">
              {!confirmSuspend ? (
                <button
                  onClick={() => setConfirmSuspend(true)}
                  className="w-full py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition"
                >
                  {t('platform.tenantSuspend')}
                </button>
              ) : (
                <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 space-y-2">
                  <p className="text-xs text-red-300">{t('platform.tenantSuspendConfirm')}</p>
                  <div className="flex gap-2">
                    <button onClick={handleSuspend}
                      className="flex-1 py-1.5 rounded text-xs font-medium bg-red-600 text-white">
                      Ja, suspendieren
                    </button>
                    <button onClick={() => setConfirmSuspend(false)}
                      className="flex-1 py-1.5 rounded text-xs text-slate-400">
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab: Revenue */}
      {activeTab === 'revenue' && (
        <TenantRevenueTab
          data={data}
          token={token}
          t={t}
          editSplit={editSplit}
          setEditSplit={setEditSplit}
          handleSave={handleSave}
          saving={saving}
        />
      )}

      {/* Tab: Features */}
      {activeTab === 'features' && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            {FEATURE_OPTIONS.map(f => (
              <label key={f.key} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800 cursor-pointer hover:bg-slate-700 transition">
                <span className="text-xs text-slate-300">{t(`platform.${f.labelKey}`)}</span>
                <input
                  type="checkbox"
                  checked={!!editFeatures[f.key]}
                  onChange={() => setEditFeatures(prev => ({ ...prev, [f.key]: !prev[f.key] }))}
                  className="accent-blue-500 w-4 h-4"
                />
              </label>
            ))}
          </div>
          <button
            onClick={handleSave} disabled={saving}
            className="w-full py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 transition"
          >
            {saving ? '…' : t('platform.tenantSave')}
          </button>
        </div>
      )}

      {/* Tab: Customers */}
      {activeTab === 'customers' && (
        <div className="space-y-3">
          <PlMiniKpi label={t('platform.kpiTotalCustomers')} value={data.customers} />
          {data.top_users && data.top_users.length > 0 && (
            <div className="rounded-lg border border-slate-700 bg-slate-800 divide-y divide-slate-700">
              {data.top_users.map(u => (
                <div key={u.id} className="px-3 py-2 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-white">{u.name}</span>
                    <span className="text-slate-500 ml-2">{u.email}</span>
                  </div>
                  <span className="text-slate-400 text-[10px]">
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('de-DE') : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: API Key */}
      {activeTab === 'apikey' && (
        <ApiKeyTab token={token} t={t} tenantId={tenantId} />
      )}

      {/* Tab: Branding */}
      {activeTab === 'branding' && (
        <BrandingTab token={token} t={t} tenantId={tenantId} initialData={data} />
      )}
    </div>
  );
}

// ── API Key Tab ───────────────────────────────────────────────

function ApiKeyTab({ token, t, tenantId }) {
  const [status, setStatus] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiClient.get(`/api/v1/aif/platform/tenants/${tenantId}/api-key/status`)
      .then(r => setStatus(r.data)).catch(() => {});
  }, [tenantId, token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/api/v1/aif/platform/tenants/${tenantId}/api-key`, { api_key: keyInput }, {
        entityType: 'tenant', queueOnOffline: true,
      });
      setKeyInput('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      setStatus(s => s ? { ...s, configured: true, warning: null } : s);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
  };

  const handleRemove = async () => {
    try {
      await apiClient.delete(`/api/v1/aif/platform/tenants/${tenantId}/api-key`, {
        entityType: 'tenant', queueOnOffline: true,
      });
      setStatus(s => s ? { ...s, configured: false, warning: 'No key configured' } : s);
      setConfirmRemove(false);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  return (
    <div className="space-y-4">
      {/* Status */}
      <div className="rounded-lg border border-slate-700 bg-slate-800 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${status?.configured ? 'bg-green-400' : 'bg-red-500'}`} />
          <span className={`text-sm font-medium ${status?.configured ? 'text-green-400' : 'text-red-400'}`}>
            {status?.configured ? t('platform.apiKeyConfigured') : t('platform.apiKeyMissing')}
          </span>
        </div>
        {status?.last_used && (
          <PlDetailRow label={t('platform.apiKeyLastUsed')} value={new Date(status.last_used).toLocaleDateString('de-DE')} />
        )}
        {status?.total_cost_30d_eur > 0 && (
          <PlDetailRow label={t('platform.apiKeyCost30d')} value={`${status.total_cost_30d_eur.toFixed(4)} €`} />
        )}
        {status?.warning && (
          <p className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-1 rounded">{t('platform.apiKeyWarning')}</p>
        )}
      </div>

      {/* Set key */}
      <div className="space-y-2">
        <label className="text-[10px] text-slate-500 uppercase tracking-wide">{t('platform.apiKeyInputLabel')}</label>
        <div className="flex gap-2">
          <input
            type="password"
            value={keyInput}
            onChange={e => setKeyInput(e.target.value)}
            placeholder={t('platform.apiKeyInputPlaceholder')}
            className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white font-mono outline-none focus:border-blue-500"
          />
          <button
            onClick={handleSave}
            disabled={saving || !keyInput.startsWith('sk-ant-')}
            className="px-3 py-2 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40"
          >
            {saving ? '…' : saved ? '✓' : t('platform.apiKeySet')}
          </button>
        </div>
        <p className="text-[10px] text-slate-500">Format: sk-ant-api03-...</p>
      </div>

      {/* Remove key */}
      {status?.configured && (
        <div className="pt-1">
          {!confirmRemove ? (
            <button
              onClick={() => setConfirmRemove(true)}
              className="text-xs text-red-400 hover:text-red-300 underline"
            >
              {t('platform.apiKeyRemove')}
            </button>
          ) : (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 space-y-2">
              <p className="text-xs text-red-300">{t('platform.apiKeyRemoveConfirm')}</p>
              <div className="flex gap-2">
                <button onClick={handleRemove} className="px-3 py-1.5 rounded text-xs bg-red-600 text-white">
                  {t('platform.apiKeyRemove')}
                </button>
                <button onClick={() => setConfirmRemove(false)} className="px-3 py-1.5 rounded text-xs text-slate-400">
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Branding Tab ──────────────────────────────────────────────

function BrandingTab({ token, t, tenantId, initialData }) {
  const [form, setForm] = useState({
    app_name: initialData?.app_name || 'AI Farm',
    logo_url: initialData?.logo_url || '',
    primary_color: initialData?.primary_color || '#2D5F2B',
    powered_by_visible: initialData?.powered_by_visible ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/api/v1/aif/platform/tenants/${tenantId}/branding`, {
        app_name: form.app_name,
        logo_url: form.logo_url || null,
        primary_color: form.primary_color,
        powered_by_visible: form.powered_by_visible,
      }, {
        entityType: 'tenant', queueOnOffline: true,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3">
        <PlFormRow label={t('platform.brandingAppName')}>
          <input type="text" value={form.app_name}
            onChange={e => setForm(f => ({ ...f, app_name: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white outline-none focus:border-blue-500" />
        </PlFormRow>
        <PlFormRow label={t('platform.brandingLogoUrl')}>
          <input type="url" value={form.logo_url}
            onChange={e => setForm(f => ({ ...f, logo_url: e.target.value }))}
            placeholder="https://…"
            className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white outline-none focus:border-blue-500" />
        </PlFormRow>
        <PlFormRow label={t('platform.brandingPrimaryColor')}>
          <div className="flex gap-2 items-center">
            <input type="color" value={form.primary_color}
              onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
              className="w-10 h-8 rounded cursor-pointer bg-slate-900 border border-slate-600" />
            <span className="text-xs text-slate-400 font-mono">{form.primary_color}</span>
          </div>
        </PlFormRow>
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" checked={form.powered_by_visible}
            onChange={e => setForm(f => ({ ...f, powered_by_visible: e.target.checked }))}
            className="accent-blue-500 w-4 h-4" />
          {t('platform.brandingPoweredBy')}
        </label>
      </div>

      {/* Preview */}
      <div className="rounded-lg border border-slate-600 bg-slate-900 p-3">
        <p className="text-[10px] text-slate-500 mb-2">{t('platform.brandingPreview')}</p>
        <div className="rounded p-2 text-center" style={{ backgroundColor: form.primary_color + '20', borderColor: form.primary_color + '40', border: '1px solid' }}>
          <div className="w-8 h-8 rounded-lg mx-auto mb-1 flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: form.primary_color }}>
            {(form.app_name || 'A').charAt(0)}
          </div>
          <span className="text-xs font-semibold text-white">{form.app_name}</span>
          {form.powered_by_visible && (
            <p className="text-[9px] text-slate-500 mt-1">Powered by AI Farm</p>
          )}
        </div>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="w-full py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 transition">
        {saving ? '…' : saved ? `✓ ${t('platform.brandingSaved')}` : t('platform.tenantSave')}
      </button>
    </div>
  );
}


// ── Tenant Detail Revenue Sub-Tab ─────────────────────────────

function TenantRevenueTab({ data, token, t, editSplit, setEditSplit, handleSave, saving }) {
  const [payouts, setPayouts] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ month: new Date().toISOString().slice(0, 7), amount: '', reference: '' });
  const [submitting, setSubmitting] = useState(false);
  const auth = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token || !data?.id) return;
    apiClient.get(`/api/v1/aif/platform/tenants/${data.id}/payouts`)
      .then(r => setPayouts(r.data))
      .catch(() => {});
  }, [token, data?.id]);

  const handleCreatePayout = async () => {
    if (!payoutForm.amount) return;
    setSubmitting(true);
    try {
      await apiClient.post(`/api/v1/aif/platform/tenants/${data.id}/payouts`, { ...payoutForm, amount: parseFloat(payoutForm.amount) }, {
        entityType: 'payout', queueOnOffline: true,
      });
      // Refresh
      const updated = await apiClient.get(`/api/v1/aif/platform/tenants/${data.id}/payouts`);
      setPayouts(updated.data);
      setShowForm(false);
      setPayoutForm({ month: new Date().toISOString().slice(0, 7), amount: '', reference: '' });
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkComplete = async (payoutId) => {
    try {
      await apiClient.patch(`/api/v1/aif/platform/tenants/${data.id}/payouts/${payoutId}?status=COMPLETED`, {}, {
        entityType: 'payout', queueOnOffline: true,
      });
      // Refresh
      const updated = await apiClient.get(`/api/v1/aif/platform/tenants/${data.id}/payouts`);
      setPayouts(updated.data);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  const inputCls = 'px-2 py-1.5 rounded-lg bg-slate-900 border border-slate-600 text-xs text-white outline-none focus:border-blue-500 w-full';

  return (
    <div className="space-y-3">
      {/* Revenue split summary */}
      <div className="rounded-lg border border-slate-700 bg-slate-800 p-3 space-y-2">
        <PlDetailRow label="Brutto MRR" value={`${data.mrr} €`} />
        <PlDetailRow label={`${t('platform.tenantShare')} (${data.revenue_split_pct}%)`} value={`${data.tenant_share_eur?.toFixed(2)} €`} />
        <PlDetailRow label={`${t('platform.alcedoShare')} (${100 - data.revenue_split_pct}%)`} value={`${data.alcedo_share_eur?.toFixed(2)} €`} accent />
      </div>

      {/* Edit revenue split */}
      <div className="space-y-2">
        <label className="text-[10px] text-slate-500 uppercase tracking-wide">{t('platform.tenantRevenueSplit')} (%)</label>
        <div className="flex gap-2">
          <input
            type="number" min="0" max="100" value={editSplit}
            onChange={e => setEditSplit(e.target.value)}
            className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white outline-none focus:border-blue-500"
          />
          <button
            onClick={handleSave} disabled={saving}
            className="px-3 py-2 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40"
          >
            {saving ? '…' : t('platform.tenantSave')}
          </button>
        </div>
      </div>

      {/* Payout history */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-500 uppercase tracking-wide">{t('platform.payoutHistory')}</span>
          <button
            onClick={() => setShowForm(v => !v)}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            {t('platform.payoutRecord')}
          </button>
        </div>

        {showForm && (
          <div className="rounded-lg border border-slate-700 bg-slate-900 p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500">{t('platform.payoutMonth')}</label>
                <input type="month" value={payoutForm.month} onChange={e => setPayoutForm(f => ({ ...f, month: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">{t('platform.payoutAmount')} (€)</label>
                <input type="number" step="0.01" value={payoutForm.amount} onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-slate-500">{t('platform.payoutReference')}</label>
              <input type="text" value={payoutForm.reference} onChange={e => setPayoutForm(f => ({ ...f, reference: e.target.value }))} placeholder="Stripe transfer ID…" className={inputCls} />
            </div>
            <button
              onClick={handleCreatePayout} disabled={submitting || !payoutForm.amount}
              className="w-full py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40"
            >
              {submitting ? '…' : t('platform.payoutCreate')}
            </button>
          </div>
        )}

        {payouts && (
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {payouts.payouts?.length === 0 && (
              <p className="text-[11px] text-slate-500 italic">Noch keine Auszahlungen erfasst.</p>
            )}
            {payouts.payouts?.map(p => (
              <div key={p.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-slate-800 border border-slate-700">
                <div>
                  <span className="text-xs text-white font-medium">{p.month}</span>
                  <span className="text-xs text-slate-400 ml-2">{p.amount.toFixed(2)} €</span>
                  {p.reference && <span className="text-[10px] text-slate-500 ml-2">{p.reference}</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${p.status === 'COMPLETED' ? 'bg-green-900 text-green-300' : 'bg-amber-900 text-amber-300'}`}>
                    {p.status === 'COMPLETED' ? t('platform.payoutCompleted') : t('platform.payoutPending')}
                  </span>
                  {p.status === 'PENDING' && (
                    <button
                      onClick={() => handleMarkComplete(p.id)}
                      className="text-[10px] text-green-400 hover:text-green-300"
                    >
                      ✓
                    </button>
                  )}
                </div>
              </div>
            ))}
            {payouts.payouts?.length > 0 && (
              <p className="text-[10px] text-slate-500 pt-1">
                Gesamt ausgezahlt: <strong className="text-white">{(payouts.total_paid || 0).toFixed(2)} €</strong>
                {payouts.pending_count > 0 && ` · ${payouts.pending_count} ausstehend`}
              </p>
            )}
          </div>
        )}
      </div>

      <p className="text-[10px] text-slate-500">{t('platform.revenueNote')}</p>
    </div>
  );
}


// ── Revenue Tab (Sprint 46: full P&L) ─────────────────────────

function PlatformRevenue({ token, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const auth = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/aif/platform/revenue', { headers: auth })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch('/api/v1/aif/platform/revenue/export', { headers: auth });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ALCEDO_Revenue_${new Date().toISOString().slice(0, 7)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPdf = async () => {
    const res = await fetch('/api/v1/aif/platform/revenue/report', { headers: auth });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ALCEDO_Platform_Report_${new Date().toISOString().slice(0, 7)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <PlatformSpinner />;
  if (!data) return <PlatformError message={t('platform.loadError')} />;

  const s = data.platform_summary || {};
  const ue = data.unit_economics || {};
  const perTenant = data.per_tenant || [];
  const trend = data.trend_6m || [];
  const marginPos = (s.net_margin || 0) >= 0;

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">{t('platform.revenue')}</h2>
        <div className="flex gap-2">
          <button
            onClick={handleExportCsv} disabled={exporting}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-40 border border-slate-600"
          >
            {exporting ? '…' : t('platform.exportCsv')}
          </button>
          <button
            onClick={handleDownloadPdf}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-700 text-white hover:bg-blue-600 border border-blue-600"
          >
            {t('platform.reportDownload')}
          </button>
        </div>
      </div>

      {/* Summary KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <PlatformKpi value={`${(s.gross_mrr || 0).toFixed(2)} €`} label={t('platform.grossMrr')} icon="💶" />
        <PlatformKpi value={`${(s.total_tenant_payouts || 0).toFixed(2)} €`} label={t('platform.tenantPayouts')} icon="➡️" />
        <PlatformKpi value={`${(s.total_alcedo_share || 0).toFixed(2)} €`} label={t('platform.kpiAlcedoShare')} icon="🏦" accent="blue" />
        <PlatformKpi value={`${(s.total_api_costs || 0).toFixed(4)} €`} label={t('platform.kpiApiCosts')} icon="🤖" />
        <PlatformKpi
          value={`${(s.net_margin || 0).toFixed(2)} €`}
          label={`${t('platform.kpiMargin')} (${s.margin_pct || 0}%)`}
          icon={marginPos ? '✅' : '⚠️'}
          accent={marginPos ? 'green' : 'red'}
        />
      </div>

      {/* Per-tenant full breakdown table */}
      <div className="rounded-xl border border-slate-700 overflow-hidden">
        <div className="bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
          {t('platform.perTenantSummary')}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-800/80">
              <tr>
                <th className="text-left px-3 py-2 text-slate-400">{t('platform.colTenantName')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colPaying')}</th>
                <th className="text-right px-3 py-2 text-slate-400">Gross MRR</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colTenantPayout')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colAlcedoShare')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colApiCost')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colMargin')}</th>
              </tr>
            </thead>
            <tbody>
              {perTenant.map((row, i) => (
                <tr key={row.tenant_id} className={`border-t border-slate-700 ${i % 2 === 0 ? 'bg-slate-900' : 'bg-slate-800/40'}`}>
                  <td className="px-3 py-2 text-white font-medium">{row.tenant_name}</td>
                  <td className="px-3 py-2 text-right text-slate-300">{row.customers_paying}</td>
                  <td className="px-3 py-2 text-right text-slate-300">{row.gross_mrr > 0 ? `${row.gross_mrr.toFixed(2)} €` : '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{row.tenant_payout > 0 ? `${row.tenant_payout.toFixed(2)} €` : '—'}</td>
                  <td className="px-3 py-2 text-right text-blue-400">{row.alcedo_share > 0 ? `${row.alcedo_share.toFixed(2)} €` : '—'}</td>
                  <td className="px-3 py-2 text-right text-amber-400">{row.api_cost_30d > 0 ? `${row.api_cost_30d.toFixed(4)} €` : '—'}</td>
                  <td className={`px-3 py-2 text-right font-medium ${row.margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {row.margin.toFixed(2)} €
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Unit economics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <PlMiniKpi label={t('platform.revPerCustomer')} value={`${(ue.avg_revenue_per_customer || 0).toFixed(2)} €`} />
        <PlMiniKpi label={t('platform.apiCostPerCustomer')} value={`${(ue.avg_api_cost_per_customer || 0).toFixed(4)} €`} />
        <PlMiniKpi label={t('platform.marginPerCustomer')} value={`${(ue.avg_margin_per_customer || 0).toFixed(2)} €`} />
        <PlMiniKpi label={t('platform.colPaying')} value={ue.paying_customers_total || 0} />
      </div>

      {/* 6-month trend table */}
      {trend.length > 0 && (
        <div className="rounded-xl border border-slate-700 overflow-hidden">
          <div className="bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
            {t('platform.trend6m')}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-800/80">
                <tr>
                  <th className="text-left px-3 py-2 text-slate-400">Monat</th>
                  <th className="text-right px-3 py-2 text-slate-400">Gross</th>
                  <th className="text-right px-3 py-2 text-slate-400">Payout</th>
                  <th className="text-right px-3 py-2 text-slate-400">ALCEDO</th>
                  <th className="text-right px-3 py-2 text-slate-400">API €</th>
                  <th className="text-right px-3 py-2 text-slate-400">Margin</th>
                </tr>
              </thead>
              <tbody>
                {trend.map((row, i) => (
                  <tr key={row.month} className={`border-t border-slate-700 ${i % 2 === 0 ? 'bg-slate-900' : 'bg-slate-800/40'}`}>
                    <td className="px-3 py-2 text-white">{row.month}</td>
                    <td className="px-3 py-2 text-right text-slate-300">{row.gross.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-right text-slate-400">{row.tenant_payout.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-right text-blue-400">{row.alcedo_share.toFixed(2)} €</td>
                    <td className="px-3 py-2 text-right text-amber-400">{row.api_cost.toFixed(4)} €</td>
                    <td className={`px-3 py-2 text-right font-medium ${row.margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {row.margin.toFixed(2)} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[10px] text-slate-500">{t('platform.revenueNote')}</p>
    </div>
  );
}


// ── Data Health Tab (A4: Datenquellen-Gesundheit) ──────────────

function DataHealthStatusPill({ status, t }) {
  const styles = {
    ok: 'bg-green-500/20 text-green-400 border-green-500/30',
    stale: 'bg-red-500/20 text-red-400 border-red-500/30',
    incomplete: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30',
    never: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${styles[status] || styles.never}`}>
      {t(`dataHealth.status_${status}`, status)}
    </span>
  );
}

function RelativeTime({ isoString, t }) {
  if (!isoString) return <span className="text-slate-500 text-xs">{t('dataHealth.never')}</span>;
  const diff = Date.now() - new Date(isoString).getTime();
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(hours / 24);
  if (hours < 1) return <span className="text-xs text-slate-300">{t('dataHealth.justNow')}</span>;
  if (hours < 24) return <span className="text-xs text-slate-300">{t('dataHealth.hoursAgo', { count: hours })}</span>;
  return <span className="text-xs text-slate-300">{t('dataHealth.daysAgo', { count: days })}</span>;
}

function PlatformDataHealth({ token, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    apiClient.get('/api/v1/aif/admin/data-freshness')
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <PlatformSpinner />;
  if (!data) return <PlatformError message={t('platform.loadError')} />;

  const sources = data.source_registry || [];
  const market = data.market_prices || {};

  return (
    <div className="space-y-4 max-w-3xl">
      <h2 className="text-lg font-semibold text-white">{t('dataHealth.title')}</h2>
      <p className="text-xs text-slate-400">{t('dataHealth.subtitle')}</p>

      {/* Market prices summary */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-300">{t('dataHealth.marketPrices')}</span>
          <DataHealthStatusPill status={market.status === 'fresh' ? 'ok' : market.status === 'stale' ? 'stale' : 'never'} t={t} />
        </div>
        {market.last_update && (
          <div className="text-[10px] text-slate-500 mt-1">
            {t('dataHealth.lastUpdate')}: {new Date(market.last_update).toLocaleString()}
          </div>
        )}
      </div>

      {/* Source registry table */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('dataHealth.sourceRegistry')}</h3>
        </div>
        <div className="divide-y divide-slate-700">
          {sources.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">{t('dataHealth.noSources')}</div>
          )}
          {sources.map(src => {
            const isWeather = src.alert_threshold_hours === 0;
            return (
              <div key={src.provider_key} className="px-4 py-3 space-y-1.5">
                {/* Row 1: name + status + tier */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-200">{src.provider_name}</span>
                  <DataHealthStatusPill
                    status={isWeather ? 'ok' : src.last_ingest_status}
                    t={t}
                  />
                  {src.trust_tier && (
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                      src.trust_tier === 'T1' ? 'bg-green-500/15 text-green-400' :
                      src.trust_tier === 'T2' ? 'bg-slate-500/15 text-slate-400' :
                      'bg-amber-500/15 text-amber-400'
                    }`}>
                      {src.tier_label || src.trust_tier}
                    </span>
                  )}
                  {!src.is_active && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-slate-600/30 text-slate-500">{t('dataHealth.inactive')}</span>
                  )}
                </div>
                {/* Row 2: details */}
                <div className="flex items-center gap-4 text-[10px] text-slate-500 flex-wrap">
                  <span>{src.provider_type}</span>
                  {isWeather ? (
                    <span className="italic">{t('dataHealth.availabilityMonitored')}</span>
                  ) : (
                    <>
                      <span>{t('dataHealth.lastIngest')}: <RelativeTime isoString={src.last_ingest_at} t={t} /></span>
                      {src.alert_threshold_hours > 0 && (
                        <span>{t('dataHealth.threshold')}: {src.alert_threshold_hours}h</span>
                      )}
                    </>
                  )}
                  {src.consecutive_failures > 0 && (
                    <span className="text-red-400">{t('dataHealth.failures')}: {src.consecutive_failures}</span>
                  )}
                  {src.expected_count && (
                    <span>{t('dataHealth.expected')}: {src.expected_count}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reference data categories */}
      {data.categories && data.categories.length > 0 && (
        <div className="rounded-xl border border-slate-700 bg-slate-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('dataHealth.referenceData')}</h3>
          </div>
          <div className="divide-y divide-slate-700">
            {data.categories.map((cat, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs text-slate-300 truncate">{cat.name}</div>
                  <div className="text-[10px] text-slate-500">{cat.source} · {cat.record_count} {t('dataHealth.records')}</div>
                </div>
                <DataHealthStatusPill
                  status={cat.verification_status === 'VERIFIED' ? 'ok' : cat.verification_status === 'RECENT' ? 'ok' : cat.verification_status === 'STALE' ? 'stale' : 'failed'}
                  t={t}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] text-slate-600 text-right">
        {t('dataHealth.generatedAt')}: {data.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}
      </div>
    </div>
  );
}


// ── System Tab (Sprint 47: full infra status) ──────────────────

function PlatformSystem({ token, t }) {
  const [dash, setDash] = useState(null);
  const [costs, setCosts] = useState(null);
  const [status, setStatus] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetch('/api/v1/aif/platform/dashboard', { headers: auth }).then(r => r.ok ? r.json() : null),
      fetch('/api/v1/aif/platform/costs', { headers: auth }).then(r => r.ok ? r.json() : null),
      fetch('/api/v1/aif/platform/system/status', { headers: auth }).then(r => r.ok ? r.json() : null),
      fetch('/api/v1/aif/platform/alerts', { headers: auth }).then(r => r.ok ? r.json() : null),
    ]).then(([d, c, s, a]) => {
      setDash(d);
      setCosts(c);
      setStatus(s);
      setAlerts(a);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  if (loading) return <PlatformSpinner />;
  if (!dash) return <PlatformError message={t('platform.loadError')} />;

  const health = dash.system_health || {};
  const costPerTenant = costs?.per_tenant || [];
  const costAlerts = costs?.cost_alerts || [];
  const disk = status?.disk || {};
  const dbStats = status?.database || {};
  const redisStats = status?.redis || {};
  const containers = status?.containers || [];
  const alertList = alerts?.alerts || [];
  const criticalCount = alerts?.critical_count || 0;
  const warningCount = alerts?.warning_count || 0;

  return (
    <div className="space-y-4 max-w-3xl">
      <h2 className="text-lg font-semibold text-white">{t('platform.system')}</h2>

      {/* Health alerts banner */}
      {(criticalCount > 0 || warningCount > 0) && (
        <div className={`rounded-xl border p-3 ${criticalCount > 0 ? 'border-red-700 bg-red-950/30' : 'border-amber-700 bg-amber-950/30'}`}>
          <p className={`text-xs font-semibold mb-2 ${criticalCount > 0 ? 'text-red-400' : 'text-amber-400'}`}>
            {criticalCount > 0 ? '🔴' : '⚠️'} {t('platform.alertsTitle')} ({criticalCount + warningCount})
          </p>
          {alertList.filter(a => a.code !== 'ALL_OK').map((a, i) => (
            <div key={i} className={`text-xs ${a.level === 'CRITICAL' ? 'text-red-300' : 'text-amber-300'}`}>
              [{a.level}] {a.message}
            </div>
          ))}
        </div>
      )}

      {/* Infrastructure status */}
      {status && (
        <div className="rounded-xl border border-slate-700 bg-slate-800 p-4 space-y-3">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('platform.systemStatus')}</h3>

          {/* Disk */}
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">{t('platform.systemDisk')}</span>
              <span className={`font-medium ${disk.status === 'ok' ? 'text-green-400' : disk.status === 'warning' ? 'text-amber-400' : 'text-red-400'}`}>
                {disk.used_pct}% ({disk.used_gb} / {disk.total_gb} GB)
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full ${disk.used_pct >= 90 ? 'bg-red-500' : disk.used_pct >= 75 ? 'bg-amber-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(disk.used_pct || 0, 100)}%` }}
              />
            </div>
          </div>

          {/* DB stats */}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <PlMiniKpi label={t('platform.systemDbSize')} value={dbStats.status === 'ok' ? `${dbStats.db_size_mb} MB` : '?'} />
            <PlMiniKpi label={t('platform.systemActiveConns')} value={dbStats.active_connections ?? '?'} />
            <PlMiniKpi label={t('platform.systemApiCost30d')} value={`${(status.api_cost_30d_eur || 0).toFixed(4)} €`} />
          </div>

          {/* Redis */}
          <PlDetailRow label={t('platform.systemRedisInfo')}
            value={redisStats.status === 'ok' ? `${redisStats.used_memory_mb} MB · ${redisStats.connected_clients} clients` : '?'} />

          {/* Containers */}
          {containers.length > 0 && (
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">{t('platform.systemContainers')}</p>
              <div className="space-y-1">
                {containers.map(c => (
                  <div key={c.name} className="flex items-center justify-between text-xs py-1 border-b border-slate-700">
                    <span className="text-slate-300">{c.name}</span>
                    <span className="text-slate-400">{c.cpu_pct} CPU · {c.mem}</span>
                    <span className="text-green-400 text-[10px]">●</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-slate-700 bg-slate-800 p-4 space-y-4">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{t('platform.systemHealth')}</h3>
        <div className="space-y-3">
          <SystemRow label={t('platform.healthDb')} status={health.db} t={t} />
          <SystemRow label={t('platform.healthRedis')} status={health.redis} t={t} />
          <div className="flex items-center justify-between py-2 border-b border-slate-700">
            <span className="text-sm text-slate-300">{t('platform.healthContainers')}</span>
            <span className="text-sm font-semibold text-white">{health.containers} running</span>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-800 p-4 space-y-2">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Platform Stats</h3>
        <PlDetailRow label={t('platform.kpiTotalCustomers')} value={dash.customers_total} />
        <PlDetailRow label={t('platform.kpiActiveCustomers30d')} value={dash.customers_active_30d} />
        <PlDetailRow label={t('platform.kpiApiCosts')} value={`${(dash.total_api_cost_30d || 0).toFixed(4)} €`} />
      </div>

      {/* Cost alerts */}
      {costAlerts.length > 0 && (
        <div className="rounded-xl border border-amber-700 bg-amber-950/30 p-3">
          <p className="text-xs font-semibold text-amber-400 mb-2">⚠️ Cost Alerts</p>
          {costAlerts.map(a => (
            <div key={a.tenant_id} className="text-xs text-amber-300">
              {a.tenant_name}: {a.cost_eur.toFixed(4)} € / 30d ({a.daily_avg.toFixed(4)} €/day)
            </div>
          ))}
        </div>
      )}

      {/* Per-tenant cost breakdown */}
      {costPerTenant.length > 0 && (
        <div className="rounded-xl border border-slate-700 overflow-hidden">
          <div className="bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide">
            {t('platform.costBreakdown')}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-slate-800/80">
              <tr>
                <th className="text-left px-3 py-2 text-slate-400">{t('platform.colTenantName')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.colMessages')}</th>
                <th className="text-right px-3 py-2 text-slate-400">{t('platform.kpiApiCosts')}</th>
                <th className="text-right px-3 py-2 text-slate-400">Own Key</th>
              </tr>
            </thead>
            <tbody>
              {costPerTenant.map((row, i) => (
                <tr key={row.tenant_id} className={`border-t border-slate-700 ${i % 2 === 0 ? 'bg-slate-900' : 'bg-slate-800/40'}`}>
                  <td className="px-3 py-2 text-white">{row.tenant_name}</td>
                  <td className="px-3 py-2 text-right text-slate-300">{row.messages_30d}</td>
                  <td className="px-3 py-2 text-right text-amber-400">{row.cost_eur.toFixed(4)} €</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`text-xs ${row.has_own_key ? 'text-green-400' : 'text-red-400'}`}>
                      {row.has_own_key ? '✓' : '✗'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SystemRow({ label, status, t }) {
  const isOk = status === 'ok';
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-700">
      <span className="text-sm text-slate-300">{label}</span>
      <div className="flex items-center gap-1.5">
        <div className={`w-2 h-2 rounded-full ${isOk ? 'bg-green-400' : 'bg-red-400'}`} />
        <span className={`text-xs font-medium ${isOk ? 'text-green-400' : 'text-red-400'}`}>
          {isOk ? t('platform.healthOk') : t('platform.healthError')}
        </span>
      </div>
    </div>
  );
}


// ── Shared helpers ────────────────────────────────────────────

function PlatformKpi({ value, label, icon, accent }) {
  const accentColor = {
    green: 'text-green-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
  }[accent] || 'text-white';

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800 p-3 text-center">
      <div className="text-lg mb-0.5">{icon}</div>
      <div className={`text-lg font-bold ${accentColor}`}>
        {value != null ? (typeof value === 'number' ? value.toLocaleString('de-DE') : value) : '—'}
      </div>
      <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{label}</div>
    </div>
  );
}

function PlMiniKpi({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800 p-2.5">
      <div className="text-[10px] text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-white mt-0.5">{value ?? '—'}</div>
    </div>
  );
}

function PlDetailRow({ label, value, accent }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-[10px] text-slate-500">{label}</span>
      <span className={`text-xs font-medium ${accent ? 'text-blue-400' : 'text-slate-200'}`}>{value}</span>
    </div>
  );
}

function PlFormRow({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function TenantStatusBadge({ status, t }) {
  const styles = {
    active: 'bg-green-500/20 text-green-400',
    suspended: 'bg-red-500/20 text-red-400',
    inactive: 'bg-slate-700 text-slate-500',
  };
  const labels = {
    active: t('platform.statusActive'),
    suspended: t('platform.statusSuspended'),
    inactive: t('platform.statusInactive'),
  };
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${styles[status] || styles.inactive}`}>
      {labels[status] || status}
    </span>
  );
}

function HealthDot({ label, status }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2.5 h-2.5 rounded-full ${status === 'ok' ? 'bg-green-400' : 'bg-red-500 animate-pulse'}`} />
      <span className={`text-xs ${status === 'ok' ? 'text-green-400' : 'text-red-400'}`}>{label}</span>
    </div>
  );
}

function PlatformSpinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function PlatformError({ message }) {
  return (
    <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-400">
      {message}
    </div>
  );
}


// ── Audit Log Tab ─────────────────────────────────────────────

function PlatformAuditLog({ token, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const auth = { Authorization: `Bearer ${token}` };

  const load = (p = 1, action = actionFilter) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, page_size: 50 });
    if (action) params.set('action', action);
    apiClient.get(`/api/v1/aif/platform/audit-log?${params}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { if (token) load(1); }, [token]);

  const handleExport = () => {
    const params = new URLSearchParams();
    if (actionFilter) params.set('action', actionFilter);
    const a = document.createElement('a');
    a.href = `/api/v1/aif/platform/audit-log/export?${params}`;
    a.setAttribute('download', '');
    // add auth via fetch + blob
    fetch(`/api/v1/aif/platform/audit-log/export?${params}`, { headers: auth })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        a.href = url;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const ACTION_LABELS = {
    TENANT_CREATE: '🏗️ TENANT_CREATE',
    TENANT_UPDATE: '✏️ TENANT_UPDATE',
    TENANT_SUSPEND: '🚫 TENANT_SUSPEND',
    API_KEY_SET: '🔑 API_KEY_SET',
    API_KEY_REMOVE: '🗑️ API_KEY_REMOVE',
    API_KEY_ROTATED: '🔄 API_KEY_ROTATED',
    RATE_LIMIT_UPDATED: '⚡ RATE_LIMIT_UPDATED',
    PAYOUT_CREATE: '💶 PAYOUT_CREATE',
    PAYOUT_COMPLETE: '✅ PAYOUT_COMPLETE',
    PLATFORM_SETTINGS_UPDATE: '⚙️ PLATFORM_SETTINGS_UPDATE',
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">{t('platform.auditLog')}</h2>
        <button onClick={handleExport} className="text-xs text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded-lg border border-slate-700">
          {t('platform.auditExportCsv')}
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <select
          value={actionFilter}
          onChange={e => { setActionFilter(e.target.value); load(1, e.target.value); }}
          className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm text-white outline-none focus:border-blue-500"
        >
          <option value="">{t('platform.auditFilterAction')} — all</option>
          {Object.entries(ACTION_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {loading ? <PlatformSpinner /> : !data ? <PlatformError message={t('platform.loadError')} /> : (
        <>
          <div className="rounded-xl border border-slate-700 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-800">
                <tr>
                  <th className="text-left px-3 py-2 text-slate-400">{t('platform.auditTimestamp')}</th>
                  <th className="text-left px-3 py-2 text-slate-400">{t('platform.auditUser')}</th>
                  <th className="text-left px-3 py-2 text-slate-400">{t('platform.auditAction')}</th>
                  <th className="text-left px-3 py-2 text-slate-400">{t('platform.auditTarget')}</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-slate-500 italic">
                      {t('platform.auditNoEntries')}
                    </td>
                  </tr>
                )}
                {data.entries.map(e => (
                  <tr key={e.id} className="border-t border-slate-800 hover:bg-slate-800/40">
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                      {e.created_at ? new Date(e.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-300">{e.user_email || '—'}</td>
                    <td className="px-3 py-2">
                      <span className="font-mono text-[10px] text-blue-300">{ACTION_LABELS[e.action] || e.action}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-400">
                      {e.target_type && <span className="text-slate-500">{e.target_type}/</span>}
                      {e.target_id ? e.target_id.slice(0, 8) + '…' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Total: {data.total} entries</span>
            <div className="flex gap-2">
              <button
                onClick={() => { const p = Math.max(1, page - 1); setPage(p); load(p); }}
                disabled={page === 1}
                className="px-2 py-1 rounded bg-slate-800 border border-slate-700 disabled:opacity-40 hover:bg-slate-700"
              >‹</button>
              <span className="px-2 py-1">Page {page}</span>
              <button
                onClick={() => { const p = page + 1; setPage(p); load(p); }}
                disabled={page * 50 >= data.total}
                className="px-2 py-1 rounded bg-slate-800 border border-slate-700 disabled:opacity-40 hover:bg-slate-700"
              >›</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ── Platform Settings Tab ──────────────────────────────────────

function PlatformSettings({ token, t }) {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const auth = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/aif/platform/settings', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setSettings(d); setForm(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const r = await apiClient.patch('/api/v1/aif/platform/settings', {
        registration_enabled: form.registration_enabled,
        maintenance_mode: form.maintenance_mode,
        global_rate_limit_messages_per_day: parseInt(form.global_rate_limit_messages_per_day),
        alert_email: form.alert_email || null,
      }, {
        entityType: 'platform_settings', queueOnOffline: true,
      });
      if (r?.data) {
        setSettings(r.data);
        setForm(r.data);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PlatformSpinner />;
  if (!form) return <PlatformError message={t('platform.loadError')} />;

  const inputCls = 'w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-600 text-sm text-white outline-none focus:border-blue-500';

  return (
    <div className="space-y-5 max-w-xl">
      <h2 className="text-lg font-semibold text-white">{t('platform.systemSettings')}</h2>

      <div className="rounded-xl border border-slate-700 bg-slate-800 p-4 space-y-4">

        {/* Registration */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-200">{t('platform.settingRegistration')}</p>
            <p className="text-xs text-slate-500 mt-0.5">Allow new users to register via /auth/register</p>
          </div>
          <button
            onClick={() => setForm(f => ({ ...f, registration_enabled: !f.registration_enabled }))}
            className={`relative w-11 h-6 rounded-full transition ${form.registration_enabled ? 'bg-blue-600' : 'bg-slate-600'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.registration_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Maintenance mode */}
        <div className="flex items-center justify-between border-t border-slate-700 pt-3">
          <div>
            <p className="text-sm text-slate-200">{t('platform.settingMaintenance')}</p>
            <p className="text-xs text-slate-500 mt-0.5">Returns 503 for all non-admin requests</p>
          </div>
          <button
            onClick={() => setForm(f => ({ ...f, maintenance_mode: !f.maintenance_mode }))}
            className={`relative w-11 h-6 rounded-full transition ${form.maintenance_mode ? 'bg-amber-500' : 'bg-slate-600'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.maintenance_mode ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Rate limit */}
        <div className="border-t border-slate-700 pt-3">
          <label className="text-sm text-slate-200 block mb-1.5">{t('platform.settingRateLimit')}</label>
          <input
            type="number" min="1" max="10000" value={form.global_rate_limit_messages_per_day || ''}
            onChange={e => setForm(f => ({ ...f, global_rate_limit_messages_per_day: e.target.value }))}
            className={inputCls}
            placeholder="50"
          />
        </div>

        {/* Alert email */}
        <div className="border-t border-slate-700 pt-3">
          <label className="text-sm text-slate-200 block mb-1.5">{t('platform.settingAlertEmail')}</label>
          <input
            type="email" value={form.alert_email || ''}
            onChange={e => setForm(f => ({ ...f, alert_email: e.target.value }))}
            className={inputCls}
            placeholder="alerts@alcedo.io"
          />
        </div>
      </div>

      <button
        onClick={handleSave} disabled={saving}
        className="w-full py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 transition"
      >
        {saving ? '…' : saved ? `✓ ${t('platform.settingSaved')}` : t('platform.settingSave')}
      </button>
    </div>
  );
}


// ── Distribution Partners ─────────────────────────────────────

function PlatformDistributionPartners({ token, t }) {
  const [dps, setDps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/v1/aif/distribution-partners', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { distribution_partners: [] })
      .then(data => { setDps(data.distribution_partners || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">{t('platform.partners', { defaultValue: 'Distribution Partners' })}</h2>
        <span className="text-xs text-slate-500">{dps.length} {t('platform.total', { defaultValue: 'gesamt' })}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-800">
              <th className="pb-2 font-medium">{t('platform.dp.name', { defaultValue: 'Name' })}</th>
              <th className="pb-2 font-medium">{t('platform.dp.region', { defaultValue: 'Region' })}</th>
              <th className="pb-2 font-medium text-right">{t('platform.dp.tenants', { defaultValue: 'Kunden' })}</th>
              <th className="pb-2 font-medium text-right">{t('platform.dp.split', { defaultValue: 'Split %' })}</th>
              <th className="pb-2 font-medium">{t('platform.dp.status', { defaultValue: 'Status' })}</th>
            </tr>
          </thead>
          <tbody>
            {dps.map(dp => (
              <tr key={dp.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition">
                <td className="py-2.5 text-white">
                  {dp.name}
                  {dp.is_root && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">ROOT</span>
                  )}
                </td>
                <td className="py-2.5 text-slate-400">{dp.region}</td>
                <td className="py-2.5 text-right text-slate-300">{dp.tenant_count}</td>
                <td className="py-2.5 text-right text-slate-300">{dp.revenue_split_pct}%</td>
                <td className="py-2.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    dp.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {dp.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
